import React, { useState, useEffect } from 'react';
import { fetchDocumentContent } from '../services/googleSheets';
import { useConfig } from '../config/ConfigContext';

/**
 * KeyTakeaways component - Premium "Illuminated Wisdom" design
 * Displays key points from the lecture with magazine-quality styling
 * @param {Object} props - Component props
 * @param {string} props.url - URL to the key takeaways document
 * @returns {JSX.Element} KeyTakeaways component
 */
const KeyTakeaways = ({ url }) => {
  const { theme } = useConfig();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Derive accent colors from theme
  const primaryColor = theme.colors.primary || '#1e4d8b';
  const accentGold = '#c9a227';
  const lightBg = '#fafbfc';
  const cardBg = '#ffffff';

  useEffect(() => {
    const loadContent = async () => {
      try {
        setLoading(true);
        setError(null);

        if (url) {
          console.log('Fetching key takeaways from:', url);
          const docContent = await fetchDocumentContent(url);
          setContent(docContent);
        } else {
          setContent(null);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error loading key takeaways:', err);
        setError('Failed to load key takeaways. Please try again later.');
        setLoading(false);
      }
    };

    loadContent();
  }, [url]);

  // Premium Loading State
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '300px',
        padding: '40px',
        background: `linear-gradient(135deg, ${lightBg} 0%, #ffffff 100%)`
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: `3px solid ${lightBg}`,
          borderTop: `3px solid ${primaryColor}`,
          borderRadius: '50%',
          animation: 'kt-spin 0.8s cubic-bezier(0.5, 0, 0.5, 1) infinite'
        }}></div>
        <p style={{
          marginTop: '20px',
          color: '#666',
          fontFamily: 'Georgia, "Times New Roman", serif',
          fontStyle: 'italic',
          fontSize: '1.1em'
        }}>
          Gathering wisdom...
        </p>
        <style dangerouslySetInnerHTML={{ __html: `
          @keyframes kt-spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}} />
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div style={{
        padding: '40px',
        margin: '20px',
        background: 'linear-gradient(135deg, #fff5f5 0%, #ffffff 100%)',
        borderRadius: '16px',
        border: '1px solid #fecaca',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '2em', marginBottom: '12px' }}>⚠️</div>
        <p style={{ color: '#dc2626', fontWeight: 500 }}>{error}</p>
      </div>
    );
  }

  // Main Content Render
  if (content) {
    return (
      <div className="key-takeaways-container" style={{
        padding: '0',
        background: `linear-gradient(180deg, ${lightBg} 0%, #ffffff 100%)`,
        minHeight: '100%'
      }}>
        {/* Elegant Header Section */}
        <div style={{
          textAlign: 'center',
          padding: '48px 24px 32px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Decorative geometric pattern background */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.03,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23${primaryColor.replace('#', '')}' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            pointerEvents: 'none'
          }}></div>

          {/* Ornamental divider above title */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '20px',
            gap: '16px'
          }}>
            <div style={{
              width: '60px',
              height: '1px',
              background: `linear-gradient(90deg, transparent, ${accentGold})`
            }}></div>
            <div style={{
              width: '8px',
              height: '8px',
              background: accentGold,
              transform: 'rotate(45deg)'
            }}></div>
            <div style={{
              width: '60px',
              height: '1px',
              background: `linear-gradient(90deg, ${accentGold}, transparent)`
            }}></div>
          </div>

          {/* Main Title */}
          <h2 style={{
            color: primaryColor,
            fontSize: 'clamp(1.75rem, 4vw, 2.25rem)',
            fontWeight: 600,
            fontFamily: 'Georgia, "Times New Roman", serif',
            letterSpacing: '-0.02em',
            margin: '0 0 12px 0',
            position: 'relative'
          }}>
            Key Takeaways
          </h2>

          {/* Subtitle */}
          <p style={{
            color: '#6b7280',
            fontSize: '1.05em',
            fontStyle: 'italic',
            margin: 0,
            fontFamily: 'Georgia, "Times New Roman", serif'
          }}>
            Essential insights from this lecture
          </p>

          {/* Ornamental divider below subtitle */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: '24px',
            gap: '8px'
          }}>
            <div style={{
              width: '40px',
              height: '2px',
              background: primaryColor,
              borderRadius: '2px'
            }}></div>
            <div style={{
              width: '6px',
              height: '6px',
              background: accentGold,
              borderRadius: '50%'
            }}></div>
            <div style={{
              width: '40px',
              height: '2px',
              background: primaryColor,
              borderRadius: '2px'
            }}></div>
          </div>
        </div>

        {/* Content Area */}
        <div
          className="kt-document-content"
          dangerouslySetInnerHTML={{ __html: content }}
          style={{
            padding: '0 24px 48px',
            maxWidth: '880px',
            margin: '0 auto'
          }}
        />

        {/* Premium Styles - Principle-Driven Semantic CSS */}
        <style dangerouslySetInnerHTML={{ __html: `
          /* Google Fonts import */
          @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=Inter:wght@400;500;600&display=swap');

          /* Animation keyframes */
          @keyframes kt-fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          /* ============================================================
             PRINCIPLE-DRIVEN SEMANTIC CSS
             ============================================================
             Classes generated by cleanGoogleDocsExportedHtml:
             - .key-takeaway-item  = Complete takeaways (Title: substantial content)
             - .takeaway-title     = Standalone titles (Title: short/no content)
             - .takeaway-content   = Long explanatory text without colon
             - .doc-info           = Short metadata (headers, page numbers, etc.)
             - .doc-empty          = Empty paragraphs
             - .doc-list-item      = List items without colon structure
             ============================================================ */

          /* Base content container with counter */
          .kt-document-content .google-docs-content {
            counter-reset: takeaway-counter;
          }

          /* ============================================================
             HIDDEN ELEMENTS - Metadata & Empty Content
             ============================================================ */

          /* Hide empty paragraphs */
          .kt-document-content .doc-empty {
            display: none !important;
          }

          /* Hide metadata/info elements (page numbers, headers, short labels) */
          .kt-document-content .doc-info {
            display: none !important;
          }

          /* Hide original document headings - we have our custom header */
          .kt-document-content .doc-heading-1,
          .kt-document-content .doc-heading-2,
          .kt-document-content .doc-heading-3 {
            display: none !important;
          }

          /* Hide unclassified <div> children that contain metadata.
             Google Docs exports the header (event name, speaker, date)
             as a <div> instead of <p>, so the HTML cleaner never
             classifies it. These are always the first elements. */
          .kt-document-content .google-docs-content > div:not([class*="takeaway"]):not([class*="key-takeaway"]) {
            display: none !important;
          }

          /* ============================================================
             TAKEAWAY CARDS - The main content
             ============================================================ */

          /* Lists reset */
          .kt-document-content ul,
          .kt-document-content ol {
            list-style: none;
            padding: 0;
            margin: 0;
          }

          /* Premium Card Design for complete takeaways (Title: Content in one) */
          .kt-document-content .key-takeaway-item {
            position: relative;
            background: ${cardBg};
            border-radius: 16px;
            padding: 28px 28px 28px 90px;
            margin-bottom: 20px !important;
            box-shadow:
              0 1px 3px rgba(0, 0, 0, 0.04),
              0 4px 12px rgba(0, 0, 0, 0.04),
              0 8px 24px rgba(0, 0, 0, 0.02);
            border: 1px solid rgba(0, 0, 0, 0.04);
            transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
            animation: kt-fadeInUp 0.6s ease-out both;
            counter-increment: takeaway-counter;
            overflow: hidden;
            line-height: 1.75;
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
            font-size: 1rem;
            color: #374151;
          }

          /* Staggered animation delays for takeaway items */
          .kt-document-content .key-takeaway-item:nth-of-type(1) { animation-delay: 0.1s; }
          .kt-document-content .key-takeaway-item:nth-of-type(2) { animation-delay: 0.2s; }
          .kt-document-content .key-takeaway-item:nth-of-type(3) { animation-delay: 0.3s; }
          .kt-document-content .key-takeaway-item:nth-of-type(4) { animation-delay: 0.4s; }
          .kt-document-content .key-takeaway-item:nth-of-type(5) { animation-delay: 0.5s; }
          .kt-document-content .key-takeaway-item:nth-of-type(6) { animation-delay: 0.6s; }
          .kt-document-content .key-takeaway-item:nth-of-type(7) { animation-delay: 0.7s; }
          .kt-document-content .key-takeaway-item:nth-of-type(8) { animation-delay: 0.8s; }

          /* Large decorative number */
          .kt-document-content .key-takeaway-item::before {
            content: counter(takeaway-counter);
            position: absolute;
            left: 20px;
            top: 50%;
            transform: translateY(-50%);
            font-family: 'Cormorant Garamond', Georgia, serif;
            font-size: 3.5rem;
            font-weight: 600;
            color: ${primaryColor};
            opacity: 0.12;
            line-height: 1;
            transition: all 0.4s ease;
          }

          /* Accent bar on left */
          .kt-document-content .key-takeaway-item::after {
            content: '';
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: 4px;
            background: linear-gradient(180deg, ${primaryColor} 0%, ${accentGold} 100%);
            border-radius: 4px 0 0 4px;
            opacity: 0;
            transition: opacity 0.3s ease;
          }

          /* Hover effects */
          .kt-document-content .key-takeaway-item:hover {
            transform: translateY(-3px);
            box-shadow:
              0 4px 8px rgba(0, 0, 0, 0.06),
              0 8px 24px rgba(0, 0, 0, 0.08),
              0 16px 48px rgba(0, 0, 0, 0.04);
            border-color: rgba(0, 0, 0, 0.08);
          }

          .kt-document-content .key-takeaway-item:hover::before {
            opacity: 0.2;
            transform: translateY(-50%) scale(1.05);
            color: ${accentGold};
          }

          .kt-document-content .key-takeaway-item:hover::after {
            opacity: 1;
          }

          /* First takeaway item - featured treatment */
          .kt-document-content .key-takeaway-item:first-of-type {
            background: linear-gradient(135deg, ${cardBg} 0%, #fefefe 50%, ${lightBg} 100%);
            padding: 32px 32px 32px 100px;
            border: 1px solid rgba(${parseInt(primaryColor.slice(1,3), 16)}, ${parseInt(primaryColor.slice(3,5), 16)}, ${parseInt(primaryColor.slice(5,7), 16)}, 0.1);
          }

          .kt-document-content .key-takeaway-item:first-of-type::before {
            font-size: 4.5rem;
            opacity: 0.15;
            left: 24px;
          }

          .kt-document-content .key-takeaway-item:first-of-type::after {
            opacity: 0.6;
            width: 5px;
          }

          /* ============================================================
             TITLE + CONTENT PAIRS - Split structure handling
             ============================================================ */

          /* Standalone title (Title: with short/no content after) */
          .kt-document-content .takeaway-title {
            position: relative;
            background: ${cardBg};
            border-radius: 16px 16px 0 0;
            padding: 28px 28px 16px 90px;
            margin-bottom: 0 !important;
            margin-top: 20px;
            box-shadow:
              0 1px 3px rgba(0, 0, 0, 0.04),
              0 4px 12px rgba(0, 0, 0, 0.04);
            border: 1px solid rgba(0, 0, 0, 0.04);
            border-bottom: none;
            animation: kt-fadeInUp 0.6s ease-out both;
            counter-increment: takeaway-counter;
            font-family: 'Cormorant Garamond', Georgia, serif;
            font-size: 1.25em;
            font-weight: 600;
            color: ${primaryColor};
            line-height: 1.4;
          }

          /* Title number decoration */
          .kt-document-content .takeaway-title::before {
            content: counter(takeaway-counter);
            position: absolute;
            left: 20px;
            top: 50%;
            transform: translateY(-50%);
            font-family: 'Cormorant Garamond', Georgia, serif;
            font-size: 3.5rem;
            font-weight: 600;
            color: ${primaryColor};
            opacity: 0.12;
            line-height: 1;
          }

          /* Title accent bar */
          .kt-document-content .takeaway-title::after {
            content: '';
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: 4px;
            background: linear-gradient(180deg, ${primaryColor} 0%, ${accentGold} 50%);
            border-radius: 4px 0 0 0;
            opacity: 0.6;
          }

          /* Content following a title */
          .kt-document-content .takeaway-content {
            position: relative;
            background: ${cardBg};
            border-radius: 0 0 16px 16px;
            padding: 16px 28px 28px 90px;
            margin-bottom: 20px !important;
            margin-top: 0 !important;
            box-shadow:
              0 4px 12px rgba(0, 0, 0, 0.04),
              0 8px 24px rgba(0, 0, 0, 0.02);
            border: 1px solid rgba(0, 0, 0, 0.04);
            border-top: none;
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
            font-size: 1rem;
            color: #374151;
            line-height: 1.75;
          }

          /* Content accent bar continuation */
          .kt-document-content .takeaway-content::after {
            content: '';
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: 4px;
            background: linear-gradient(180deg, ${accentGold} 0%, transparent 100%);
            border-radius: 0 0 0 4px;
            opacity: 0.6;
          }

          /* ============================================================
             STRONG/BOLD TEXT - Title emphasis within cards
             ============================================================
             Principle: The title and colon form a semantic unit.
             Using display:inline keeps "Title:" together, then content flows after.
             ============================================================ */

          .kt-document-content .key-takeaway-item strong,
          .kt-document-content .takeaway-title strong {
            display: inline;
            font-family: 'Cormorant Garamond', Georgia, serif;
            font-size: 1.25em;
            font-weight: 600;
            color: ${primaryColor};
            letter-spacing: -0.01em;
            line-height: 1.4;
          }

          /* Colon after strong - part of the title unit */
          .kt-document-content .key-takeaway-item strong + text,
          .kt-document-content .takeaway-title strong + text {
            /* Colon stays inline with title */
          }

          /* Content after colon flows naturally */
          .kt-document-content .key-takeaway-item {
            line-height: 1.85;
          }

          /* ============================================================
             LIST ITEMS - Non-takeaway list content
             ============================================================ */

          .kt-document-content .doc-list-item {
            padding: 8px 0;
            border-bottom: 1px solid ${lightBg};
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
            color: #374151;
            line-height: 1.6;
          }

          .kt-document-content .doc-list-item:last-child {
            border-bottom: none;
          }

          /* ============================================================
             RESPONSIVE ADJUSTMENTS
             ============================================================ */

          @media (max-width: 640px) {
            /* Reclaim parent tab-wrapper padding for wider cards */
            .key-takeaways-container {
              margin-left: -8px !important;
              margin-right: -8px !important;
            }

            /* Reduce content wrapper side padding (overrides inline 24px) */
            .kt-document-content {
              padding-left: 6px !important;
              padding-right: 6px !important;
              padding-bottom: 32px !important;
            }

            /* Collapse Google Docs table wrappers */
            .kt-document-content table {
              width: 100% !important;
              border-spacing: 0;
            }

            .kt-document-content .key-takeaway-item,
            .kt-document-content .takeaway-title {
              padding: 20px 16px 16px 50px;
              border-radius: 12px;
            }

            .kt-document-content .takeaway-title {
              border-radius: 12px 12px 0 0;
              padding-bottom: 10px;
              margin-top: 16px;
            }

            .kt-document-content .takeaway-content {
              padding: 8px 16px 20px 50px;
              border-radius: 0 0 12px 12px;
            }

            .kt-document-content .key-takeaway-item::before,
            .kt-document-content .takeaway-title::before {
              font-size: 2rem;
              left: 12px;
            }

            .kt-document-content .key-takeaway-item:first-of-type {
              padding: 22px 18px 20px 54px;
              border-radius: 12px;
            }

            .kt-document-content .key-takeaway-item:first-of-type::before {
              font-size: 2.5rem;
              left: 12px;
            }

            .kt-document-content .key-takeaway-item strong,
            .kt-document-content .takeaway-title strong {
              font-size: 1.1em;
            }
          }

          /* ============================================================
             PRINT STYLES
             ============================================================ */

          @media print {
            .kt-document-content .key-takeaway-item,
            .kt-document-content .takeaway-title,
            .kt-document-content .takeaway-content {
              break-inside: avoid;
              box-shadow: none;
              border: 1px solid #e5e7eb;
            }
          }
        `}} />
      </div>
    );
  }

  // Fallback - No content available
  return (
    <div style={{
      padding: '60px 24px',
      textAlign: 'center',
      background: `linear-gradient(135deg, ${lightBg} 0%, #ffffff 100%)`
    }}>
      <div style={{
        width: '64px',
        height: '64px',
        margin: '0 auto 20px',
        background: lightBg,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '28px'
      }}>
        📝
      </div>
      <p style={{
        color: '#6b7280',
        fontFamily: 'Georgia, "Times New Roman", serif',
        fontStyle: 'italic',
        fontSize: '1.1em'
      }}>
        No key takeaways available for this lecture yet.
      </p>
    </div>
  );
};

export default KeyTakeaways;
